import {useMemo} from 'react';

import {useMultiRunWorkspaceQuery} from '../../state/graphql/multiRunWorkspaceQuery';
import {type Grouping} from '../../util/queryTypes';
import {createMultiRunDefaultSpec} from '../Workspace/createMultiRunDefaultSpec';
import {useIsWorkspaceTemplateActionDisplayed} from '../WorkspaceTemplate/useIsWorkspaceTemplateEnabled';
import {useViewerWorkspaceTemplates} from '../WorkspaceTemplate/useViewerWorkspaceTemplates';
import {
  type MultiRunWorkspaceProps,
  type MultiRunWorkspaceReadyProps,
} from './types';

type UseMultiRunWorkspaceQueryPropsResult =
  | {loading: true; error: null}
  | {loading: false; error: true}
  | {
      loading: false;
      error: null;
      readyProps: MultiRunWorkspaceReadyProps;
    };

export function useMultiRunWorkspaceQueryProps(
  props: MultiRunWorkspaceProps
): UseMultiRunWorkspaceQueryPropsResult {
  const {entityName, projectName, defaultSpecPostFn, viewType} = props;

  // We need to compute the default workspace step, which relies on first
  // loading the project.
  const multiRunWorkspaceQuery = useMultiRunWorkspaceQuery({
    entityName,
    projectName,
  });

  const {isWorkspaceTemplateActionDisplayed} =
    useIsWorkspaceTemplateActionDisplayed(viewType);
  const {workspaceTemplates} = useViewerWorkspaceTemplates(
    isWorkspaceTemplateActionDisplayed
  );

  // Templates only feed the default spec for a brand-new workspace, so they
  // must not gate loading of an existing saved view. We build the default spec
  // from whatever templates have resolved so far (createMultiRunDefaultSpec is
  // null-safe).
  //
  // This is a deliberate race: the templates query can still be in flight when
  // the workspace initializes. The outcomes are:
  // - A template that resolves after mount is synced into redux by
  //   useMultiRunWorkspaceProps. Creating a new workspace reads the default
  //   spec from redux at creation time (createNewWorkspaceAndLoad), and
  //   creation happens several query round trips after mount, so the template
  //   is almost always applied.
  // - If a brand-new workspace is created before the template resolves, that
  //   one workspace is built with plain default settings and no template
  //   values. This is the accepted worst case.
  // - Reset is unaffected: resetToDefaultSpec receives a fresh template at
  //   call time and does not read the spec built here.
  return useMemo(() => {
    if (multiRunWorkspaceQuery.loading) {
      return {loading: true, error: null};
    }

    const project = multiRunWorkspaceQuery.data?.project;

    if (project == null) {
      return {loading: false, error: true};
    }

    const recommendedGrouping: Grouping = [];

    let defaultSpec = createMultiRunDefaultSpec(workspaceTemplates[0]);
    defaultSpec.section.runSets![0].grouping = recommendedGrouping;
    if (defaultSpecPostFn != null) {
      defaultSpec = defaultSpecPostFn(defaultSpec);
    }

    return {
      loading: false,
      error: null,
      readyProps: {
        project,
        defaultSpec,
        recommendedGrouping,
      },
    };
  }, [
    multiRunWorkspaceQuery.loading,
    multiRunWorkspaceQuery.data?.project,
    workspaceTemplates,
    defaultSpecPostFn,
  ]);
}
